import { useEffect } from "react";
import type { RefObject } from "react";
import { isTextField, type PadHost } from "./padFocus";

/**
 * useTextPadDock — эффекты docked-режима TextPad (вынесено из TextPad.tsx,
 * гигант/рэтчет). Панель у низа экрана без подложки, текст живёт в поле хоста.
 *
 * 06.09 (владелец) — правила закрытия:
 *  • тап (сдвиг ≤10px, тот же pointerId) вне панели → закрыть, НО НЕ если цель
 *    тапа — поле ввода (input/textarea/contenteditable/[data-pad-host]): такое
 *    поле само откроет свою клавиатуру (кастомную или системную);
 *  • свайп/скролл (сдвиг >10px) — не закрывать: экран проматывается под падом;
 *  • focusin в input/textarea, который НЕ хост и у которого inputMode !== "none"
 *    (поле системной клавиатуры) → закрыть, иначе видны две клавиатуры;
 *  • Esc → закрыть. Перед любым закрытием хост зовёт flushChange (в close).
 */
const TAP_MAX_PX = 10;
const FIELD_SEL = "input, textarea, [contenteditable], [data-pad-host]";

export interface DockCloseOpts {
  enabled: boolean;
  dockRef: RefObject<HTMLElement | null>;
  hostRef?: RefObject<PadHost | null>;
  /** Закрыть (хост делает flushChange + onClose); читается через ref в момент события. */
  close: () => void;
}

export function useDockClose(o: DockCloseOpts): void {
  const { enabled } = o;
  useEffect(() => {
    if (!enabled) return;
    const st = { x: 0, y: 0, id: -1, out: false };
    const down = (e: PointerEvent) => {
      const t = e.target instanceof Element ? e.target : null;
      const inDock = !!t && !!o.dockRef.current?.contains(t);
      const onField = !!t?.closest?.(FIELD_SEL);
      st.out = !inDock && !onField;
      st.x = e.clientX; st.y = e.clientY; st.id = e.pointerId;
    };
    const up = (e: PointerEvent) => {
      if (!st.out || e.pointerId !== st.id) return;
      st.out = false;
      if (Math.hypot(e.clientX - st.x, e.clientY - st.y) > TAP_MAX_PX) return; /* свайп — не закрываем */
      o.close();
    };
    const cancel = () => { st.out = false; };
    const focus = (e: FocusEvent) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!isTextField(t) || t === o.hostRef?.current) return;
      if (o.dockRef.current?.contains(t)) return;
      if ((t.getAttribute("inputmode") ?? "").toLowerCase() === "none") return; /* хост другого кастомного пада */
      o.close();
    };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") o.close(); };
    document.addEventListener("pointerdown", down, true);
    document.addEventListener("pointerup", up, true);
    document.addEventListener("pointercancel", cancel, true);
    document.addEventListener("focusin", focus, true);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", down, true);
      document.removeEventListener("pointerup", up, true);
      document.removeEventListener("pointercancel", cancel, true);
      document.removeEventListener("focusin", focus, true);
      document.removeEventListener("keydown", key);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}

/**
 * 02.09 (владелец: «клавиатура перекрывает поле комментария»): docked-панель
 * публикует свою реальную высоту в --tp-dock-h — хост ставит спейсер и
 * докручивает поле ровно над клавиатурой, а не по константе.
 */
export function useDockHeight(enabled: boolean, dockRef: RefObject<HTMLElement | null>, onHeight: (h: number) => void): void {
  useEffect(() => {
    const root = document.documentElement;
    if (!enabled) { root.style.setProperty("--tp-dock-h", "0px"); return; }
    const el = dockRef.current;
    const pub = () => { const h = el ? Math.ceil(el.getBoundingClientRect().height) : 0; root.style.setProperty("--tp-dock-h", `${h}px`); onHeight(h); };
    pub();
    const ro = el && "ResizeObserver" in window ? new ResizeObserver(pub) : null;
    if (el && ro) ro.observe(el);
    return () => { ro?.disconnect(); root.style.setProperty("--tp-dock-h", "0px"); onHeight(0); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
